import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import {
  IStyles,
  pxTovW,
  pxToRem,
  TypographyHtml,
} from '@geneo2-web/shared-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { IQuestionMetadataHint } from '../../api/learners';
import SearchIcon from '@mui/icons-material/Search';
import DoneIcon from '@mui/icons-material/Done';

interface IProps {
  open: boolean;
  handleClose: () => void;
  hintData: IQuestionMetadataHint[];
}

const styles: IStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    width: { xs: pxToRem(320), md: pxTovW(600), lg: pxTovW(500) },
    // maxWidth: { xs: 'max-content' },
    // marginX: { xs: pxToRem(10), md: pxToRem(5) },
    borderRadius: pxToRem(16),
    overflowY: 'auto',
    maxHeight: '80vh',
    // position: 'fixed',
    // top: '30%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: pxToRem(10),
    padding: `${pxToRem(18)} ${pxToRem(24)}`,
    background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
    borderBottom: '1px solid #ffe082',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(10),
  },
  title: {
    // fontSize: pxToRem(20),
    fontWeight: 700,
    color: '#e69500',
  },
  content: {
    padding: pxToRem(24),
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(16),
  },
  hintBox: {
    padding: pxToRem(16),
    borderRadius: pxToRem(12),
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(8),
    transition: 'all 0.3s ease',
  },
  hintHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(8),
    fontWeight: 600,
    fontSize: pxToRem(14),
    textTransform: 'uppercase',
  },
  revealButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: pxToRem(8),
    padding: pxToRem(16),
    borderRadius: pxToRem(12),
    border: '2px dashed #cbd5e1',
    cursor: 'pointer',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  allRevealed: {
    textAlign: 'center',
    color: 'rgb(21, 128, 61)',
    display: 'flex',
    gap: pxToRem(4),
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    paddingY: pxToRem(8),
    border: '1px solid rgb(187, 247, 208)',
    backgroundColor: 'rgb(240, 253, 244)',
    borderRadius: pxToRem(10),
  },
  // title: {
  //   flex: 1,
  //   fontSize: pxToRem(18),
  //   fontWeight: 700,
  //   color: '#f57c00',
  // },
  // content: {
  //   padding: pxToRem(10),
  // },
};
interface IProps {
  open: boolean;
  handleClose: () => void;
  hintData: IQuestionMetadataHint[];
  unlockedCount: number;
  setUnlockedCount: React.Dispatch<React.SetStateAction<number>>;
}
export const HintPopupPal = (props: IProps) => {
  const { open, handleClose, hintData, unlockedCount, setUnlockedCount } =
    props;
  const { t } = useTranslation();

  // Reset hints back to 1 when the dialog is closed & reopened
  useEffect(() => {
    if (open && unlockedCount === 0) {
      setUnlockedCount(1);
    }
  }, [open]);
  // Sort: vague first -> strong second -> maintain original order if tied
  const sortedHints = [...hintData].sort((a, b) => {
    if (a.type === 'vague' && b.type !== 'vague') return -1;
    if (a.type !== 'vague' && b.type === 'vague') return 1;
    return a.order - b.order;
  });

  const handleRevealNext = () => {
    if (unlockedCount < sortedHints.length) {
      setUnlockedCount((prev) => prev + 1);
    }
  };

  // const isUnlocked = (index: number) => {
  //   return index === 0 || openedHints.includes(index - 1);
  // };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: pxToRem(16),
          overflow: 'hidden',
          // width: 'max-content',
        },
      }}
    >
      {/* <Box sx={{ display: 'flex', justifyContent: 'center' }}> */}
      <Box sx={styles.container}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.titleContainer}>
            <Box>💡</Box>
            <Typography variant="h2" sx={styles.title}>
              {t('PAL_HINT_TITLE')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ color: '#999' }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={styles.content}>
          {sortedHints.slice(0, unlockedCount).map((hint, index) => {
            const isCurrent = index === unlockedCount - 1;

            // Light green for current, grey for disabled/previous
            const boxStyles = {
              ...styles.hintBox,
              backgroundColor: isCurrent ? '#e8f5e9' : '#f8fafc',
              border: isCurrent ? '1px solid #c8e6c9' : '1px solid #e2e8f0',
            };

            const headerColor = isCurrent ? '#388e3c' : '#94a3b8';
            const iconOpacity = isCurrent ? 1 : 0.5;
            const textColor = isCurrent ? '#000' : '#94a3b8';

            return (
              <Collapse in={true} key={`hint-${hint.order || index}`}>
                <Box sx={boxStyles}>
                  <Box sx={{ ...styles.hintHeader, color: headerColor }}>
                    <Typography sx={{ opacity: iconOpacity }}>💡</Typography>
                    {t('PAL_HINT_LABEL', { n: index + 1 })}
                  </Box>
                  <TypographyHtml sx={{ color: textColor }}>
                    {hint.text}
                  </TypographyHtml>
                </Box>
              </Collapse>
            );
          })}

          {/* Reveal Next Button / Completion Text */}
          {unlockedCount < sortedHints.length ? (
            <Box sx={styles.revealButton} onClick={handleRevealNext}>
              <SearchIcon fontSize="small" />
              <Typography
                variant="smallText"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('PAL_HINT_REVEAL_NEXT', { n: unlockedCount + 1 })}
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.allRevealed}>
              <DoneIcon fontSize="small" />
              <Typography variant="smallText" color="secondary.main">
                {t('PAL_HINT_ALL_REVEALED')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
